import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import css from './AddModal.scss';
import Header from 'common/Header';
import classnames from 'classnames';
import { TextArea } from 'semantic-ui-react';
import Icon from 'SF/MaterialIcon';
import useStarfishAPI from 'hooks/useStarfishAPI';
import { SFEntityID } from 'utils/types';
import { assign, find } from 'lodash';
import { urlParams } from 'utils/request';
import { isEmptyString } from 'utils/text';
import Select from 'common/StyledSelect';
import {
    getInstructionErrorMessage,
    getNameErrorMessage,
    getTypeErrorMessage,
    isLocationValid
} from 'utils/locations';

const AddLocationModal = (props) => {
    const [ saveLocationResponse, callSaveLocationAPI ] = useStarfishAPI();
    const [ editedLocation, setEditedLocation ] = useState({});
    const [ showLocationInvalid, setShowLocationInvalid ] = useState(false);
    
    useEffect(() => {
        setEditedLocation(props.isEdit ? props.location : { name: '', instructions: '' });
        setShowLocationInvalid(false);
    }, [props.location, props.isOpen]);
    
    Modal.setAppElement(document.getElementById('main-center'));
    
    const disableSave = () =>
        showLocationInvalid && !isLocationValid(editedLocation.type, editedLocation.name, editedLocation.instructions);
    
    const save = () => {
        if (!isLocationValid(editedLocation.type, editedLocation.name, editedLocation.instructions)) {
            setShowLocationInvalid(true);
            return;
        }
        
        callSaveLocationAPI('../instructor/saveLocationPreferences.html', {
            method: 'POST',
            body: urlParams(editedLocation)
        });
    };
    
    useEffect(() => {
        if (saveLocationResponse.success) props.onSave();
    }, [saveLocationResponse]);
    
    const getError = field => {
        if (!showLocationInvalid) return '';
        switch (field) {
            case 'type':
                return getTypeErrorMessage(editedLocation.type);
            case 'name':
                return getNameErrorMessage(editedLocation.name);
            case 'instructions':
                return getInstructionErrorMessage(editedLocation.instructions);
        }
    };
    
    const getErrorInput = field => {
        const error = getError(field);
        if (isEmptyString(error)) return null;
        return <span className={css['error-message']}><Icon aria-hidden="true" icon="error"/>{error}</span>;
    };
    
    const getRequired = () => <span className={css['required']} aria-hidden={true}>*</span>;
    
    return (
        <Modal isOpen={props.isOpen}
               onRequestClose={props.closeModal}
               overlayClassName={css['overlay']}
               className={css['modal']}>
            <Header closeModal={props.closeModal} />
            <div className={classnames(css['content'], {[css.ie]: props.isIE})}>
                <h1>{props.isEdit ? 'Edit' : 'Add'} Location</h1>
                <div className={classnames({[css.error]: !isEmptyString(getError('type'))})}>
                    <label htmlFor="locationType">{getRequired()}Location Type</label>
                    <Select id="locationType"
                            ariaLabel="Location Type (required)"
                            className={css['dropdown']}
                            onChange={type =>
                                setEditedLocation(assign({}, editedLocation, {type: type.value}))
                            }
                            options={props.locationTypes}
                            placeholder="Select a Location Type"
                            value={find(props.locationTypes, ['value', editedLocation.type])} />
                    {getErrorInput('type')}
                </div>
                <div className={classnames({[css.error]: !isEmptyString(getError('name'))})}>
                    <label id="locationName">{getRequired()}Location Name</label>
                    <input type="text" 
                           aria-labelledby="locationName"
                           aria-required="true"
                           onChange={(e) =>
                               setEditedLocation(assign({}, editedLocation, {name: e.target.value})) }
                           placeholder="Provide a Location Name"
                           value={editedLocation.name} />
                    {getErrorInput('name')}
                </div>
                <div className={classnames({[css.error]: !isEmptyString(getError('instructions'))})}>
                    <label id="locationInstructions">Instructions</label>
                    <TextArea value={editedLocation.instructions}
                              aria-labelledby="locationInstructions"
                              onChange={(e) => 
                                  setEditedLocation(assign({}, editedLocation, {instructions: e.target.value})) }
                              placeholder="Provide instructions to students for this location, if any." />
                    <span>100 characters max</span>
                    {getErrorInput('instructions')}
                </div>
            </div>
            <div className={css['actions']}>
                <button data-testid="saveLocation" 
                        disabled={disableSave()}
                        onClick={() => { save() }}>
                    {props.isEdit ? 'Edit' : 'Add'} Location
                </button>
            </div>
        </Modal>
    );
};

AddLocationModal.propTypes = {
    closeModal: PropTypes.func,
    isEdit: PropTypes.bool,
    isIE: PropTypes.bool,
    isOpen: PropTypes.bool,
    location: PropTypes.shape({
        id: SFEntityID.isRequired,
        type: PropTypes.string
    }),
    locationTypes: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        text: PropTypes.string,
        value: PropTypes.string
    })),
    onSave: PropTypes.func
};

export default AddLocationModal;
